import { EmptyLearnerPage } from "../Layouts/EmptyLearnerPage";
import { useQuery } from "@tanstack/react-query";
import { executeQuery } from "../../../utils";
import {
  CoursesResponse,
  CourseGroup,
  CourseGroupWithCourses,
} from "../../types";
import { CourseCard } from "../Courses/CourseCard";
import { Loading } from "../Loading";
import { GroupedCoursesHeader } from "../Courses/GroupedCoursesHeader";

const Syllabi = () => {
  const { data: groupsData } = useQuery<{
    groups: CourseGroup[];
  }>({
    queryKey: ["groupsQuery"],
    queryFn: () =>
      executeQuery({
        path: "/learner/default/courses/groups",
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: courseData } = useQuery<CoursesResponse>({
    queryKey: ["courseQuery"],
    queryFn: () =>
      executeQuery({
        path: "/learner/default/courses",
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const courseGroups = (): CourseGroupWithCourses[] | undefined => {
    if (groupsData && courseData) {
      const groupsWithCourses: CourseGroupWithCourses[] = groupsData.groups.map(
        (group) => ({
          ...group,
          courses: [],
        })
      );
      courseData.forEach((cd) => {
        if (cd.course.groupId) {
          const group = groupsWithCourses.find(
            (g) => g._id === cd.course.groupId
          );
          cd.course.units = cd.units;
          if (group) {
            group.courses.push(cd.course);
          }
        }
      });

      return groupsWithCourses.filter((g) => g.courses.length > 0);
    }
  };

  const g = courseGroups();

  return (
    <EmptyLearnerPage title="Syllabi" dontTrackTime>
      <div className="bg-dm-gray-100 p-6">
        {g ? (
          <>
            <GroupedCoursesHeader
              groups={g}
              disableSearch
              titleOverride="DeltaMath for Home Course Descriptions and Syllabi"
            />
            <div className="relative">
              {g.map((group) => (
                <div
                  key={group._id}
                  className="relative mx-4 mb-10 flex flex-col gap-4"
                  id={group.name.replace(/\s+/g, "-").toLowerCase()}
                  // See GroupedCoursesHeader for details on how these are used
                  data-scrollspy
                  data-group-id={group._id}
                >
                  <div className="max-w-[600px]">
                    <h2 className="mb-1 font-serif text-lg font-bold">
                      {group.name}
                    </h2>
                    <p className="text-dm-charcoal-800">{group.description}</p>
                  </div>
                  <div className="relative grid grid-cols-1 content-stretch justify-stretch gap-4 md:grid-cols-2">
                    {group.courses.map((course) => (
                      <CourseCard
                        key={course.id}
                        course={course}
                        isPrepCourse={group.name.toLowerCase().includes("prep")}
                        showSyllabusButton
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <Loading />
        )}
      </div>
    </EmptyLearnerPage>
  );
};

export default Syllabi;
